import { Typography } from '@mui/material'
import { LocationCodeDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ColoredLabel,
  ColorSchema,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

interface Props {
  data: LocationCodeDto[]
  onEdit: (item: LocationCodeDto) => void
}

export const LocationCodesList = observer(({ data, onEdit }: Props) => {
  const { t } = useTranslate()
  const defaultOptions = useDefaultMRTOptions<LocationCodeDto>()

  const columns = useMemo<MRT_ColumnDef<LocationCodeDto>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('code', 'Code'),
        Cell: cell => <Typography variant='captionBold'>{cell.row.original.code}</Typography>,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'isActive',
        header: t('status', 'Status'),
        enableColumnFilterModes: false,
        Cell: cell => (
          <ColoredLabel
            sx={{ display: 'inline-block' }}
            color={(cell.row.original.isActive ? 'success' : 'warning') as ColorSchema}
            label={`${cell.row.original.isActive ? t('active', 'Active') : t('inactive', 'Inactive')}`}
          />
        ),
        filterVariant: 'select',
        filterSelectOptions: [
          { label: t('active', 'Active'), value: 'true' },
          { label: t('inactive', 'Inactive'), value: 'false' },
        ],
        filterFn: 'equals',
      },
      {
        accessorKey: 'country',
        header: t('country', 'Country'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'isPort',
        header: t('isPort', 'isPort'),
        enableColumnFilterModes: false,
        Cell: cell => (cell.row.original.isPort ? t('yes', 'yes') : t('no', 'no')),
        filterVariant: 'select',
        filterSelectOptions: [
          { label: t('yes', 'Yes'), value: 'true' },
          { label: t('no', 'No'), value: 'false' },
        ],
        filterFn: 'equals',
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } =
    useTablePreferencesConfig('planning/location-codes')

  const table = useTomTable<LocationCodeDto>(
    {
      columns,
      data,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: ['mrt-row-actions', 'mrt-row-expand', 'mrt-row-select', 'code', 'name'],
        },
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => onEdit(row.original),
            testId: 'edit-location-code-btn',
          },
        ]

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='location-code-options-btn'
          />
        )
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
